import liff from "@line/liff";
import { useMemo, useState, FormEvent } from "react";
import { Button, Col, Container, Divider, Form, Input, Message, Row } from "rsuite";
import styles from "./style.module.css"
import useAuthStatus from "../hooks/useAuthStatus";
import { useDispatch } from "react-redux";
import { actionCreators } from "../state";
import { bindActionCreators } from "redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { APIResult, User } from "../my-type";
import { getCurrentUserProfile, loginWithPassword } from "../service/AuthService";
import getTeamCodeByHost from "../util/teamCode";
import { isAllowedRiderTeamUser, RIDER_TEAM_ACCESS_DENIED_MESSAGE } from "../util/riderAccess";

//const envRedirectURI = document.location.protocol + '//' + document.location.hostname + process.env.REACT_APP_REDIRECT_URI;


switch (document.location.hostname) {
  case "localhost":
    document.title = 'Easyride(Dev)';
    break;
  case "www.easyride.tw":
    document.title = 'Easyride 機場接送';
    break;
  case "www.ridego.me":
    document.title = 'Ride Go 機場接送';
    break;
  case "ubertaxi.digi96.com":
    document.title = 'UberTaxi 老司機幫';
    break;
  case "rider.gotoairport.tw":
    document.title = 'UberTaxi 老司機幫';
    break;
  case "rider.suxi168.com":
    document.title = '舒適168 機場接送';
    break;

  default:
    break;
}

export default function Login() {
  let [searchParams] = useSearchParams()
  const location = searchParams.get("location")

  const { loggedIn, loading } = useAuthStatus({ redirectToLogin: false });
  const dispatch = useDispatch();
  const { updateUser } = bindActionCreators(actionCreators, dispatch);
  const [accountUsername, setAccountUsername] = useState<string>("");
  const [accountPassword, setAccountPassword] = useState<string>("");
  const [accountLoginError, setAccountLoginError] = useState<string | null>(null);
  const [accountLoginLoading, setAccountLoginLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const teamCode = useMemo(() => getTeamCodeByHost(), []);
  const isInLineClient = useMemo(() => liff.isInClient(), []);


  // Function to set a cookie
  const setCookieFunction = (name: string, value: string, minutes: number | undefined) => {
    let expires = "";
    if (minutes) {
      const date = new Date();
      date.setTime(date.getTime() + minutes * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + value + expires + "; path=/";
  };

  const onLoginClick = () => {

    if (!teamCode) {
      console.error("Team code not found for host, unable to proceed with LINE login");
      return;
    }

    let response_type = "code";
    const envLINEClientID = process.env[`REACT_APP_LINE_CLIENT_ID_${teamCode}`];

    let client_id = envLINEClientID;
    //let redirect_uri = envRedirectURI;
    let redirect_uri = document.location.protocol + '//' + document.location.hostname + "/line/" + teamCode + "/callback";

    if (document.location.hostname === "localhost") {
      redirect_uri = "http://localhost:3000/line/" + teamCode + "/callback";
    }

    if (location) {
      redirect_uri += `?location=${location}`;
    }


    let state = Date.now().toString();
    setCookieFunction("oauth_state", state, 5);
    let scope = "profile openid email";
    let nonce = Date.now().toString();
    setCookieFunction("oauth_nonce", nonce, 5);
    let prompt = "consent";
    prompt = "login";
    let uri = `https://access.line.me/oauth2/v2.1/authorize?response_type=${response_type}&client_id=${client_id}` +
      `&redirect_uri=${redirect_uri}&state=${state}&scope=${scope}&nonce=${nonce}&prompt=${prompt}`;

    let uriEncoded = encodeURI(uri);

    console.log(uriEncoded);

    window.location.href = uriEncoded;
  }

  const onLogout = async () => {
    console.log('logging out...');
    updateUser(null);
  }

  const handleAccountLogin = async (_formValue: Record<string, any> | null, event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setAccountLoginError(null);

    if (!teamCode) {
      setAccountLoginError("無法取得團隊代碼，請確認網址設定。");
      return;
    }

    if (!accountUsername || !accountPassword) {
      setAccountLoginError("請輸入帳號與密碼。");
      return;
    }

    try {
      setAccountLoginLoading(true);
      const loginResult = await loginWithPassword({
        teamCode,
        username: accountUsername,
        password: accountPassword
      });

      if (!loginResult || loginResult.statusCode !== 1) {
        setAccountLoginError(loginResult?.message || "登入失敗，請稍後再試。");
        return;
      }

      const token = loginResult.data?.token;
      if (!token) {
        setAccountLoginError("登入回傳資料不完整，請稍後再試。");
        return;
      }

      const profileResult: APIResult | null = await getCurrentUserProfile(token);
      if (!profileResult || profileResult.statusCode !== 1 || !profileResult.data) {
        setAccountLoginError(profileResult?.message || "取得使用者資料失敗，請稍後再試。");
        return;
      }

      const fetchedUser: User = profileResult.data;
      if (!isAllowedRiderTeamUser(fetchedUser)) {
        updateUser(null);
        setAccountLoginError(RIDER_TEAM_ACCESS_DENIED_MESSAGE);
        return;
      }

      const normalizedUser: User = {
        ...fetchedUser,
        userId: fetchedUser.userId ?? accountUsername,
        jwtToken: token,
        refreshToken: fetchedUser.refreshToken ?? "",
        lineAccessToken: fetchedUser.lineAccessToken ?? null,
        tokenExpiry: fetchedUser.tokenExpiry ? new Date(fetchedUser.tokenExpiry) : null
      };

      updateUser(normalizedUser);
      setAccountUsername("");
      setAccountPassword("");

      if (location) {
        navigate(location);
      } else {
        navigate("/rider/order/create");
      }
    } catch (error) {
      console.error("Account login failed", error);
      setAccountLoginError("登入時發生錯誤，請稍後再試。");
    } finally {
      setAccountLoginLoading(false);
    }
  }

  return (
    <div className={styles.loginPageContainer}>
      <Container>
        <Row className={styles.centerRow}>
          <Col xs={24} sm={18} md={12} lg={10} xl={8} className={styles.loginFrame}>
            {loading && (
              <div className={styles.loadingContainer}>
                <span>Login 登入中...</span>
              </div>
            )}

            {!loggedIn && !loading && (
              <div className={styles.loginContent}>
                <h4 className={styles.loginTitle}>歡迎使用</h4>
                {isInLineClient ? (
                  <>
                    <div className={styles.loginDescription}>
                      <p>請使用您的LINE帳號登入</p>
                      <p className={styles.subText}>系統透過LINE官方帳號通知您預約相關訊息</p>
                    </div>
                    <Button className={styles.loginBtn} onClick={onLoginClick}>
                      LINE 登入
                    </Button>
                    <Divider className={styles.loginDivider}>或使用帳號登入</Divider>
                  </>
                ) : (
                  <div className={styles.loginDescription}>
                    <p>請使用帳號密碼登入</p>
                  </div>
                )}
                <Form fluid className={styles.traditionalLoginForm} onSubmit={handleAccountLogin}>
                  <Form.Group className={styles.formGroup}>
                    <Form.ControlLabel>帳號</Form.ControlLabel>
                    <Input
                      value={accountUsername}
                      onChange={(value) => setAccountUsername(value)}
                      placeholder="輸入帳號"
                      name="username"
                      className={styles.inputControl}
                    />
                  </Form.Group>
                  <Form.Group className={styles.formGroup}>
                    <Form.ControlLabel>密碼</Form.ControlLabel>
                    <Input
                      value={accountPassword}
                      onChange={(value) => setAccountPassword(value)}
                      placeholder="輸入密碼"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      className={styles.inputControl}
                    />
                  </Form.Group>
                  {accountLoginError && (
                    <Message showIcon type="error" className={styles.errorMessage}>
                      {accountLoginError}
                    </Message>
                  )}
                  <Button
                    className={styles.accountLoginBtn}
                    appearance="primary"
                    type="submit"
                    loading={accountLoginLoading}
                    disabled={accountLoginLoading}
                  >
                    帳號登入
                  </Button>
                  <Button
                    appearance="link"
                    onClick={() => {
                      if (location) {
                        navigate(`/register?location=${encodeURIComponent(location)}`);
                      } else {
                        navigate("/register");
                      }
                    }}
                  >
                    建立新帳號
                  </Button>
                </Form>
              </div>
            )}

            {loggedIn && !loading && (
              <div className={styles.loggedInContent}>
                <h4 className={styles.loginTitle}>登入成功</h4>
                <p>您已經登入系統</p>
                <Button className={styles.logoutBtn} onClick={onLogout}>
                  登出
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  )
}
